import clsx from 'clsx';
import { FEATURE_FLAGS } from '@/shared/config/feature-flags';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag';
import { PlatformLogo } from '@/shared/components/promote/PlatformLogo';
import { AdPlatform } from '@/shared/types/enums';
import styles from './PlatformSelector.module.scss';

export interface PlatformSelectorProps {
  /** Currently selected platform */
  value?: AdPlatform;
  /** Change handler */
  onChange: (platform: AdPlatform) => void;
  /** Whether multiple platforms can be selected */
  multiple?: boolean;
  /** Selected platforms when in multiple mode */
  selectedPlatforms?: AdPlatform[];
  /** Disabled platforms */
  disabledPlatforms?: AdPlatform[];
  className?: string;
}

const PLATFORMS = [
  { id: AdPlatform.META, label: 'Meta', color: 'meta' },
  { id: AdPlatform.GOOGLE, label: 'Google', color: 'google' },
  { id: AdPlatform.TWITTER, label: 'X', color: 'twitter' },
] as const;

export function PlatformSelector({
  value,
  onChange,
  disabledPlatforms = [],
  className,
}: PlatformSelectorProps) {
  const xPlatformEnabled = useFeatureFlag(FEATURE_FLAGS.X_PLATFORM);
  const platforms = PLATFORMS.filter(
    (platform) => platform.id !== AdPlatform.TWITTER || xPlatformEnabled,
  );

  return (
    <div
      className={clsx(styles.container, className)}
      role="radiogroup"
      aria-label="Select platform"
    >
      {platforms.map((platform) => {
        const isDisabled = disabledPlatforms.includes(platform.id);
        const isSelected = value === platform.id;

        return (
          <button
            key={platform.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            className={clsx(
              styles.option,
              styles[platform.color],
              isSelected && styles.selected,
              isDisabled && styles.disabled,
            )}
            disabled={isDisabled}
            onClick={() => onChange(platform.id)}
          >
            <span className={styles.icon}>
              <PlatformLogo platform={platform.id} size={24} />
            </span>
            <span className={styles.label}>{platform.label}</span>
          </button>
        );
      })}
    </div>
  );
}
